import React, { useState, useEffect } from 'react';
import { Trophy, Medal, TrendingUp, TrendingDown, ChevronRight, Star } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { useNavigate } from 'react-router-dom';

export default function StoreRankingWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await apiRequest('/store-ops/store-rankings');
        setData(result);
      } catch (error) {
        console.error('Failed to load store rankings');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Card data-testid="store-ranking-widget">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            Store Rankings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-12 bg-slate-200 rounded w-full"></div>
            <div className="h-8 bg-slate-200 rounded w-full"></div>
            <div className="h-8 bg-slate-200 rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const rankings = data?.rankings || [];
  const topPerformer = data?.top_performer;
  const needsAttention = data?.needs_attention || [];

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="h-4 w-4 text-yellow-500" />;
    if (rank === 2) return <Medal className="h-4 w-4 text-gray-400" />;
    if (rank === 3) return <Medal className="h-4 w-4 text-amber-600" />;
    return <span className="text-xs font-bold text-slate-400">#{rank}</span>;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <Card data-testid="store-ranking-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-yellow-500" />
            Store Rankings
          </span>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/store-rankings')}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Top Performer Highlight */}
        {topPerformer && (
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-3 rounded-lg border border-yellow-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                <span className="font-semibold text-yellow-800">#1 {topPerformer.store}</span>
              </div>
              <span className="text-xl font-bold text-yellow-600">{topPerformer.overall_score}%</span>
            </div>
            <div className="grid grid-cols-4 gap-1 text-xs">
              <div className="text-center">
                <div className="font-medium">{topPerformer.scores?.compliance || 0}</div>
                <div className="text-yellow-600">Compliance</div>
              </div>
              <div className="text-center">
                <div className="font-medium">{topPerformer.scores?.attendance || 0}</div>
                <div className="text-yellow-600">Attendance</div>
              </div>
              <div className="text-center">
                <div className="font-medium">{topPerformer.scores?.efficiency || 0}</div>
                <div className="text-yellow-600">Efficiency</div>
              </div>
              <div className="text-center">
                <div className="font-medium">{topPerformer.scores?.customer || 0}</div>
                <div className="text-yellow-600">Customer</div>
              </div>
            </div>
          </div>
        )}

        {/* Rankings List */}
        {rankings.length > 0 ? (
          <div className="space-y-2">
            <div className="text-xs font-medium text-slate-500 uppercase">{data?.period}</div>
            {rankings.slice(0, 5).map((store, idx) => (
              <div 
                key={idx} 
                className={`flex items-center justify-between p-2 rounded-lg ${
                  store.rank === 1 ? 'bg-yellow-50' : 'bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-6 flex justify-center">
                    {getRankIcon(store.rank)}
                  </div>
                  <div>
                    <span className="font-medium text-sm">{store.store}</span>
                    {store.trend && (
                      <span className="ml-2">
                        {store.trend === 'up' ? (
                          <TrendingUp className="h-3 w-3 inline text-green-500" />
                        ) : store.trend === 'down' ? (
                          <TrendingDown className="h-3 w-3 inline text-red-500" />
                        ) : null}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center">
                    {[1,2,3,4,5].map(i => (
                      <Star 
                        key={i} 
                        className={`h-3 w-3 ${i <= Math.round(store.overall_score / 20) ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`} 
                      />
                    ))}
                  </div>
                  <span className={`font-bold text-sm ${getScoreColor(store.overall_score)}`}>
                    {store.overall_score}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-slate-500">
            <Trophy className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No ranking data available</p>
          </div>
        )}

        {/* Needs Attention */}
        {needsAttention.length > 0 && (
          <div className="border-t pt-3 mt-3">
            <div className="text-xs font-medium text-red-600 mb-2">Needs Attention</div>
            {needsAttention.slice(0, 2).map((store, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs py-1">
                <span>{store.store}</span>
                <Badge variant="destructive" className="text-xs">{store.overall_score}%</Badge>
              </div>
            ))}
          </div>
        )}

        {/* Scoring Weights */}
        <div className="border-t pt-3 text-xs text-slate-400">
          Score weights: Compliance 25% • Attendance 20% • Efficiency 25% • Customer 30%
        </div>
      </CardContent>
    </Card>
  );
}
